import { type ComboboxData, Select } from "@mantine/core";
import { useRichTextEditorContext } from "@mantine/tiptap";
import { useState } from "react";
import type { Required } from "utility-types";
import type { ExpressionEditorProps } from "../ExpressionEditor";
import type { FormulaData } from "../ExpressionEditor.types";

export const ID_FOR_NO_TEMPLATE = "noTemplate";

interface ExpressionTemplateSelectProps extends Required<
  Pick<ExpressionEditorProps, "invalid" | "required" | "formulaTemplates">,
  "formulaTemplates"
> {
  initialFormulaData?: FormulaData;
}

function ExpressionTemplateSelect({
  formulaTemplates,
  initialFormulaData,
  required,
  invalid
}: ExpressionTemplateSelectProps) {
  const hasInitialFormula =
    initialFormulaData?.formula && initialFormulaData.formula !== "";

  let initialTemplateId = initialFormulaData?.template_id || null;
  if (!initialTemplateId && hasInitialFormula)
    initialTemplateId = ID_FOR_NO_TEMPLATE;

  const [value, setValue] = useState(initialTemplateId);

  const { editor } = useRichTextEditorContext();

  if (formulaTemplates.length < 1 || !editor) return null;

  const formulaTemplateOptions: ComboboxData = [
    { label: "keine Vorlage", value: ID_FOR_NO_TEMPLATE },
    ...formulaTemplates.map((template) => ({
      label: template.name,
      value: template.id
    }))
  ];

  function changeTemplateIdInStorage(newTemplateId: string | null) {
    // transaction-based storage modification only needed when it should work atomically
    // (without any extra editor updates like setEditable, pasteText etc.)
    editor?.view.dispatch(
      editor.state.tr.setMeta("customValueInTemplate", {
        templateId: newTemplateId
      })
    );
  }

  if (initialTemplateId) {
    // set initial editor state
    editor?.on("create", () => {
      changeTemplateIdInStorage(initialTemplateId);

      if (initialTemplateId === ID_FOR_NO_TEMPLATE) return;

      // only "real" template needs the full init cycle
      handleChangeTemplate(
        initialTemplateId,
        initialFormulaData?.custom_values
      );
    });
  }

  function handleChangeTemplate(
    newTemplateId: string | null,
    newCustomValues?: Record<string, string | null>
  ) {
    if (!editor) return;

    editor.chain().focus().clearContent().run();

    setValue(newTemplateId);
    editor.storage.customValueInTemplate.templateId = newTemplateId;

    if (!newTemplateId || newTemplateId === ID_FOR_NO_TEMPLATE) {
      editor.setEditable(true);
    } else {
      const newTemplate = formulaTemplates.find(
        (template) => template.id === newTemplateId
      );
      if (!newTemplate) return;

      const newTemplateCustomValues =
        newTemplate.custom_value_names &&
        newTemplate.custom_value_names.length > 0
          ? Object.fromEntries(
              newTemplate.custom_value_names.map((key) => [
                key,
                newCustomValues?.[key] || null
              ])
            )
          : {};

      editor.storage.pasteTransform.allowedCustomValues =
        newTemplate.custom_value_names || [];
      editor.storage.customValueInTemplate.values = newTemplateCustomValues;

      // use the paste pipeline to transform template into correct nodes
      editor.view.pasteText(newTemplate.formula);

      editor.setEditable(false);
    }
  }

  return (
    <Select
      data={formulaTemplateOptions}
      error={required && invalid && !value}
      label="Formelvorlage"
      mb="md"
      placeholder="Bitte auswählen..."
      value={value}
      onChange={(newValue) => handleChangeTemplate(newValue)}
    />
  );
}

export { ExpressionTemplateSelect };
